import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
};

const Page = styled.div`
  padding: 1.75rem 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1600px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const HeroRow = styled.section`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 1.25rem;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #f5f7ff 0%, #eff3ff 100%);
  border: 1px solid #e5edff;
  border-radius: 14px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeroTitle = styled.h2`
  margin: 0 0 0.4rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #4b5563;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.875rem;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
`;

const StatHint = styled.div`
  font-size: 0.75rem;
  color: ${({ tone }) =>
    tone === 'positive' ? '#16a34a' : tone === 'warn' ? '#d97706' : '#6b7280'};
  font-weight: 500;
`;

const TwoCol = styled.section`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 1.25rem;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1.25rem 1.1rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const MoreLink = styled(Link)`
  font-size: 0.8rem;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: #2563eb;
  }
`;

const FileRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.875rem;
  align-items: center;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: auto 1fr auto;
    & > :nth-child(3) {
      display: none;
    }
  }
`;

const FileIconBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ color }) => color};
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const FileName = styled.span`
  font-size: 0.9rem;
  color: #111827;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
  white-space: nowrap;
`;

const Donut = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 1.25rem;
  align-items: center;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const DonutSvg = styled.svg`
  width: 160px;
  height: 160px;
`;

const DonutCenter = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DonutText = styled.div`
  position: absolute;
  text-align: center;
`;

const DonutValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
`;

const DonutLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 2px;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const LegendItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.85rem;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

const LegendLabel = styled.span`
  color: #4b5563;
`;

const LegendValue = styled.span`
  color: #111827;
  font-weight: 600;
`;

const LegendPct = styled.span`
  color: #6b7280;
  font-weight: 500;
  min-width: 48px;
  text-align: right;
`;

const SectionTitle = styled.h3`
  margin: 0.5rem 0 0.875rem;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const ServerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.875rem;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ServerCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ServerHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const ServerLogo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#374151'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
  flex-shrink: 0;
`;

const ServerName = styled.span`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: ${({ status }) =>
    status === 'warn' ? '#fef3c7' : status === 'error' ? '#fee2e2' : '#dcfce7'};
  color: ${({ status }) =>
    status === 'warn' ? '#92400e' : status === 'error' ? '#991b1b' : '#166534'};
`;

const Region = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const ServerUsage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.25rem;
`;

const UsageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.8rem;
`;

const UsageLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const UsageVal = styled.span`
  color: #111827;
  font-weight: 600;
`;

const UsageBar = styled.div`
  height: 6px;
  background: #f3f4f6;
  border-radius: 3px;
  overflow: hidden;
`;

const UsageFill = styled.div`
  width: ${({ pct }) => Math.min(100, pct)}%;
  height: 100%;
  background: ${({ color }) => color || 'linear-gradient(90deg, #3b82f6, #2563eb)'};
`;

const UsagePct = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
`;

const NotificationItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const NotifIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ tone }) => (tone === 'warn' ? '#fef3c7' : '#dcfce7')};
  color: ${({ tone }) => (tone === 'warn' ? '#d97706' : '#16a34a')};
  flex-shrink: 0;
`;

const NotifText = styled.span`
  font-size: 0.85rem;
  color: #374151;
`;

const NotifTime = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
`;

const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const QuickItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.1rem 0.5rem;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: all 0.15s ease;

  &:hover {
    background: #eff6ff;
    border-color: #dbeafe;
    transform: translateY(-1px);
  }
`;

const QuickIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuickLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
`;

const QuickHint = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
`;

const FILE_COLORS = {
  pdf: '#ef4444',
  jpg: '#a855f7',
  jpeg: '#a855f7',
  png: '#a855f7',
  xlsx: '#16a34a',
  xls: '#16a34a',
  csv: '#16a34a',
  zip: '#f59e0b',
  rar: '#f59e0b',
  doc: '#2563eb',
  docx: '#2563eb',
  default: '#6b7280',
};

const getExt = (name) => {
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  return name.slice(idx + 1).toLowerCase();
};

const getServerStyle = (type, name) => {
  const key = `${type || ''} ${name || ''}`.toLowerCase();
  if (key.includes('aws') || key.includes('s3')) return { bg: '#232f3e', color: '#ff9900', short: 'aws' };
  if (key.includes('cloudflare') || key.includes('r2')) return { bg: '#fff7ed', color: '#f38020', short: 'CF' };
  if (key.includes('hetzner')) return { bg: '#d50c2d', color: '#ffffff', short: 'H' };
  if (key.includes('cinetree')) return { bg: '#f5f3ff', color: '#7c3aed', short: 'CT' };
  if (key.includes('skynet') || key.includes('play')) return { bg: '#eff6ff', color: '#2563eb', short: 'SK' };
  return { bg: '#f3f4f6', color: '#374151', short: (name || '?').slice(0, 2).toUpperCase() };
};

const formatTB = (gb) => {
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  return `${gb} GB`;
};

const Donut2 = ({ usedPct, color = '#2563eb' }) => {
  const radius = 60;
  const stroke = 14;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (usedPct / 100) * circumference;
  return (
    <DonutCenter>
      <DonutSvg viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 80 80)"
        />
      </DonutSvg>
    </DonutCenter>
  );
};

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const FolderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const ServerStackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const RECENT_FILES = [
  { name: '회사소개서_최종.pdf', size: '2.4 MB', when: '2시간 전' },
  { name: '제품이미지_001.jpg', size: '1.8 MB', when: '3시간 전' },
  { name: '보고서_2024.xlsx', size: '856 KB', when: '5시간 전' },
  { name: '프로젝트_자료.zip', size: '45.2 MB', when: '1일 전' },
  { name: '시스템_설계서.docx', size: '1.2 MB', when: '1일 전' },
];

const SERVERS = [
  { name: 'AWS S3', type: 'aws', region: 'ap-northeast-2', usedGB: 45.2, totalGB: 1024, status: 'ok' },
  { name: 'play.skynet', type: 'skynet', region: '서울, 한국', usedGB: 128.7, totalGB: 2048, status: 'ok' },
  { name: 'file.cinetree.kr', type: 'cinetree', region: '서울, 한국', usedGB: 256.3, totalGB: 2048, status: 'ok' },
  { name: 'Hetzner', type: 'hetzner', region: '독일, 유럽', usedGB: 1228.8, totalGB: 3072, status: 'warn' },
  { name: 'Cloudflare R2', type: 'cloudflare', region: 'Global', usedGB: 847.1, totalGB: 2048, status: 'ok' },
];

const NOTIFICATIONS = [
  { tone: 'warn', text: 'AWS S3 서버의 저장소 사용량이 80%를 초과했습니다.', time: '1시간 전' },
  { tone: 'ok', text: 'file.cinetree.kr 서버 연결이 정상입니다.', time: '2시간 전' },
];

const formatGB = (gb) => {
  if (gb >= 1024) return `${(gb / 1024).toFixed(1)} TB`;
  return `${gb.toFixed(1)} GB`;
};

const Dashboard = ({ drives = [] }) => {
  const totals = useMemo(() => {
    const totalUsedGB = SERVERS.reduce((s, x) => s + x.usedGB, 0);
    const totalCapGB = SERVERS.reduce((s, x) => s + x.totalGB, 0);
    return {
      serverCount: SERVERS.length,
      totalUsedGB,
      totalCapGB,
      usedPct: Math.round((totalUsedGB / totalCapGB) * 1000) / 10,
      fileCount: 12458,
    };
  }, []);

  const remainingPct = Math.round((100 - totals.usedPct) * 10) / 10;

  return (
    <Page>
      <HeroRow>
        <Hero>
          <HeroTitle>안녕하세요, 관리자님! 👋</HeroTitle>
          <HeroSubtitle>파일서버 시스템에 오신 것을 환영합니다.</HeroSubtitle>
        </Hero>
        <StatGrid>
          <StatCard>
            <StatLabel>전체 서버</StatLabel>
            <StatValue>{totals.serverCount}</StatValue>
            <StatHint tone="positive">활성</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>총 저장소</StatLabel>
            <StatValue>{formatTB(totals.totalCapGB)}</StatValue>
            <StatHint>사용 가능</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>사용 중</StatLabel>
            <StatValue>{formatTB(totals.totalUsedGB)}</StatValue>
            <StatHint tone="warn">{totals.usedPct}%</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>전체 파일</StatLabel>
            <StatValue>{totals.fileCount.toLocaleString()}</StatValue>
            <StatHint>개</StatHint>
          </StatCard>
        </StatGrid>
      </HeroRow>

      <TwoCol>
        <Card>
          <CardHeader>
            <CardTitle>최근 업로드 파일</CardTitle>
            <MoreLink to="/FileView">더보기 →</MoreLink>
          </CardHeader>
          {RECENT_FILES.map((file) => {
            const ext = getExt(file.name);
            const color = FILE_COLORS[ext] || FILE_COLORS.default;
            return (
              <FileRow key={file.name}>
                <FileIconBadge color={color}>{ext.toUpperCase().slice(0, 3)}</FileIconBadge>
                <FileName>{file.name}</FileName>
                <FileMeta>{file.size}</FileMeta>
                <FileMeta>{file.when}</FileMeta>
              </FileRow>
            );
          })}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>저장소 사용 현황</CardTitle>
          </CardHeader>
          <Donut>
            <DonutCenter>
              <Donut2 usedPct={totals.usedPct} />
              <DonutText>
                <DonutValue>{formatTB(totals.totalUsedGB)}</DonutValue>
                <DonutLabel>사용 중</DonutLabel>
              </DonutText>
            </DonutCenter>
            <Legend>
              <LegendItem>
                <Dot color="#2563eb" />
                <LegendLabel>사용 중</LegendLabel>
                <LegendValue>{formatTB(totals.totalUsedGB)}</LegendValue>
                <LegendPct>{totals.usedPct}%</LegendPct>
              </LegendItem>
              <LegendItem>
                <Dot color="#e5e7eb" />
                <LegendLabel>사용 가능</LegendLabel>
                <LegendValue>{formatTB(totals.totalCapGB - totals.totalUsedGB)}</LegendValue>
                <LegendPct>{remainingPct}%</LegendPct>
              </LegendItem>
              <LegendItem>
                <Dot color="#9ca3af" />
                <LegendLabel>총 용량</LegendLabel>
                <LegendValue>{formatTB(totals.totalCapGB)}</LegendValue>
                <LegendPct>100%</LegendPct>
              </LegendItem>
            </Legend>
          </Donut>
        </Card>
      </TwoCol>

      <section>
        <SectionTitle>서버 상태</SectionTitle>
        <ServerGrid>
          {SERVERS.map((srv) => {
            const style = getServerStyle(srv.type, srv.name);
            const pct = Math.round((srv.usedGB / srv.totalGB) * 1000) / 10;
            return (
              <ServerCard key={srv.name}>
                <ServerHead>
                  <ServerLogo bg={style.bg} color={style.color}>{style.short}</ServerLogo>
                  <ServerName>{srv.name}</ServerName>
                  <StatusBadge status={srv.status}>
                    {srv.status === 'warn' ? '경고' : srv.status === 'error' ? '오류' : '정상'}
                  </StatusBadge>
                </ServerHead>
                <Region>{srv.region}</Region>
                <ServerUsage>
                  <UsageRow>
                    <UsageLabel>사용량</UsageLabel>
                    <UsagePct>{pct}%</UsagePct>
                  </UsageRow>
                  <UsageRow>
                    <UsageVal>
                      {formatGB(srv.usedGB)} / {formatTB(srv.totalGB)}
                    </UsageVal>
                  </UsageRow>
                  <UsageBar>
                    <UsageFill
                      pct={pct}
                      color={
                        pct >= 80
                          ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                          : 'linear-gradient(90deg, #3b82f6, #2563eb)'
                      }
                    />
                  </UsageBar>
                </ServerUsage>
              </ServerCard>
            );
          })}
        </ServerGrid>
      </section>

      <TwoCol>
        <Card>
          <CardHeader>
            <CardTitle>시스템 알림</CardTitle>
            <MoreLink to="/Config">더보기 →</MoreLink>
          </CardHeader>
          {NOTIFICATIONS.map((n, idx) => (
            <NotificationItem key={idx}>
              <NotifIcon tone={n.tone}>
                {n.tone === 'warn' ? <WarnIcon /> : <CheckIcon />}
              </NotifIcon>
              <NotifText>{n.text}</NotifText>
              <NotifTime>{n.time}</NotifTime>
            </NotificationItem>
          ))}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>빠른 작업</CardTitle>
          </CardHeader>
          <QuickGrid>
            <QuickItem to="/Upload">
              <QuickIcon><UploadIcon /></QuickIcon>
              <QuickLabel>파일 업로드</QuickLabel>
              <QuickHint>파일을 업로드합니다</QuickHint>
            </QuickItem>
            <QuickItem to="/FileView">
              <QuickIcon><FolderIcon /></QuickIcon>
              <QuickLabel>파일 보기</QuickLabel>
              <QuickHint>업로드된 파일 목록</QuickHint>
            </QuickItem>
            <QuickItem to="/Servers">
              <QuickIcon><ServerStackIcon /></QuickIcon>
              <QuickLabel>서버 관리</QuickLabel>
              <QuickHint>서버 설정 관리</QuickHint>
            </QuickItem>
            <QuickItem to="/Config">
              <QuickIcon><UsersIcon /></QuickIcon>
              <QuickLabel>사용자 관리</QuickLabel>
              <QuickHint>사용자 계정 관리</QuickHint>
            </QuickItem>
          </QuickGrid>
        </Card>
      </TwoCol>
    </Page>
  );
};

export default Dashboard;
